import useSummaryConfig, {
  type SummaryConfigs,
  type SummaryType,
} from '@ctw/shared/api/ai/use-config';
import { Button } from '@ctw/shared/components/button';
import { Heading } from '@ctw/shared/components/heading';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useDrawer } from '@ctw/shared/context/drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Create a new component for the drawer content
const ConfigDrawerContent = ({
  initialConfig,
  onConfigChange,
}: {
  onClose: () => void;
  initialConfig: SummaryConfigs | null;
  onConfigChange: (configs: SummaryConfigs) => void;
}) => {
  // Now we can use useState in a proper component
  const [configs, setConfigs] = useState(initialConfig || null);

  // Update local state when props change
  useEffect(() => {
    setConfigs(initialConfig || null);
  }, [initialConfig]);

  const handleTextChange = (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!configs) {
      return;
    }

    // switch on key
    switch (key) {
      case 'DefaultSummaryType': {
        const newConfigs = { ...configs, DefaultSummaryType: value as SummaryType };
        setConfigs(newConfigs);
        onConfigChange(newConfigs);
        break;
      }
      case 'Timeframe': {
        const newConfigs = { ...configs, Timeframe: value || '' };
        setConfigs(newConfigs);
        onConfigChange(newConfigs);
        break;
      }
      case 'Checklist': {
        const newConfigs = { ...configs, Checklist: value || '' };
        setConfigs(newConfigs);
        onConfigChange(newConfigs);
        break;
      }
      default:
        break;
    }
  };

  const configForm = Object.entries(configs || {})
    .filter(([key]) => key !== 'EntityID')
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => {
      return (
        <div key={key} className={tw`flex flex-col gap-2 pt-2 pt-2 capitalize`}>
          <label htmlFor={key}>{key}</label>
          <input
            id={key}
            type="text"
            value={value || ''}
            onChange={(e) => handleTextChange(key, e)}
          />
        </div>
      );
    });

  return (
    <div>
      <Heading level="h3">Summary Config</Heading>
      <form>{configForm}</form>
    </div>
  );
};

export interface SummaryConfigDrawerProps {
  configs: SummaryConfigs;
}

export const useSummaryConfigDrawer = () => {
  const telemetry = useTelemetry();
  const [currentConfigs, setCurrentConfigs] = useState<SummaryConfigs | null>(null);
  // Use a ref to track the latest configs for submission
  const latestConfigsRef = useRef<SummaryConfigs | null>(null);

  const { requestContext } = useCTW();
  const [builderId, setBuilderId] = useState<string>(requestContext?.builderId || '');

  const {
    configs: fetchedConfigs,
    updateConfigs,
    fetchConfigs,
  } = useSummaryConfig(builderId || '');

  // Moved from drawer content to here

  const { openDrawer, closeDrawer, isDrawerOpen } = useDrawer({
    boundaryName: 'SummaryConfigDrawer',
  });

  // Update local state when fetched configs change
  useEffect(() => {
    if (fetchedConfigs) {
      setCurrentConfigs(fetchedConfigs);
      latestConfigsRef.current = fetchedConfigs;
    }
  }, [fetchedConfigs]);

  const handleConfigsChange = (configs: SummaryConfigs) => {
    // Ensure we're setting the current configs correctly
    setCurrentConfigs(configs);
    // Update the ref with the latest configs
    latestConfigsRef.current = configs;
  };

  const handleSubmit = () => {
    // Config changes have already been propagated to parent components
    if (fetchedConfigs && builderId) {
      // Create a new payload with the current config values from the ref
      const latestConfigs = latestConfigsRef.current;

      if (!latestConfigs) {
        return;
      }

      updateConfigs(latestConfigs)
        .then(() => {
          closeDrawer();
        })
        .catch(() => {
          toast.error('Error updating configs');
        });
    } else {
      closeDrawer();
    }
  };

  return {
    openDrawer: ({
      builderId: drawerBuilderId,
    }: {
      builderId?: string;
    }) => {
      // Set builderId for useSummaryConfig
      if (drawerBuilderId) {
        setBuilderId(drawerBuilderId);
      }

      // Fetch latest configs before opening
      fetchConfigs().finally(() => {
        openDrawer({
          telemetry,
          action: {
            content: {
              body: (
                <ConfigDrawerContent
                  onClose={closeDrawer}
                  initialConfig={currentConfigs}
                  onConfigChange={handleConfigsChange}
                />
              ),
              footer: (
                <div className={tw`flex justify-end gap-2`}>
                  <Button type="button" variant="secondary" onClick={closeDrawer}>
                    Close
                  </Button>
                  <Button type="button" variant="primary" onClick={handleSubmit}>
                    Update
                  </Button>
                </div>
              ),
            },
          },
        });
      });
    },
    isDrawerOpen,
  };
};
